/** Your account (design v2 § 8): the plan panel with Manage billing, this month's meters with
 * holds hatched, what a hold is, and what happens when a plan ends. Code holders have no
 * portal; they see the code's terms instead. */
import type { Api, Entitlement, Meter as MeterData } from "../api/api";
import { CellMeter, Meter } from "./Meter";
import { kindOf, tierName } from "./PlanBadge";
import "../styles/account.css";

const NAMES: Record<string, string> = {
  pages_ingested: "Pages of textbook this month",
  papers_generated: "Practice papers this month",
  subjects: "Subjects",
};
const ORDER = ["pages_ingested", "papers_generated", "subjects"];
/** Past this many places the subjects cells would be hairlines; a bar reads better. */
const MAX_CELLS = 12;

/** The plan in full words; the badge's short form belongs in the header only. */
function planName(e: Entitlement): string {
  switch (kindOf(e)) {
    case "none":
      return "No plan yet";
    case "read_only":
      return "Read-only";
    case "trial":
      return e.chosen_plan ? `Free trial of ${tierName(e.chosen_plan)}` : "Free trial";
    case "complimentary":
      return "Complimentary access";
    case "paid":
      return `${tierName(e.chosen_plan ?? e.plan)} plan`;
  }
}

function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function dayMonth(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long" });
}

/** "Renews 14 October 2026", "Trial ends …", "Until …", or nothing. */
function when(e: Entitlement): string {
  if (!e.ends_at) return "";
  switch (kindOf(e)) {
    case "trial":
      return `Trial ends ${longDate(e.ends_at)}`;
    case "complimentary":
      return `Until ${longDate(e.ends_at)}`;
    case "paid":
      return (e.renews ?? true) ? `Renews ${longDate(e.ends_at)}` : `Ends ${longDate(e.ends_at)}`;
    default:
      return "";
  }
}

function MeterRow({ m }: { m: MeterData }) {
  const label = NAMES[m.name] ?? m.name;
  if (m.name === "subjects" && m.limit > 0 && m.limit <= MAX_CELLS) {
    return <CellMeter label={label} meter={m} />;
  }
  return (
    <Meter
      label={label}
      meter={m}
      large
      heldWords={m.name === "pages_ingested" ? "held for work in progress" : "held"}
    />
  );
}

export function AccountScreen({
  api,
  entitlement,
  email,
  onSubscribe,
}: {
  api: Api;
  entitlement: Entitlement;
  email?: string;
  onSubscribe: () => void;
}) {
  const e = entitlement;
  async function manage() {
    const r = await api.portal();
    if (r.ok) window.location.assign(r.data.url);
  }
  const kind = kindOf(e);
  const line = [when(e), email].filter(Boolean).join(" · ");
  const meters = [...e.meters].sort((a, b) => ORDER.indexOf(a.name) - ORDER.indexOf(b.name));
  const resets = meters.find((m) => m.resets_on)?.resets_on;
  return (
    <main className="account">
      <h1 className="account__title">Your account</h1>
      <section className="account__plan" aria-label="Your plan">
        <div className="account__plan-text">
          <strong className="account__plan-name">{planName(e)}</strong>
          {line && <span className="account__plan-line">{line}</span>}
          {kind === "complimentary" && (
            <span className="account__plan-line">
              {e.ends_at
                ? "When your access ends, choose a plan to keep going."
                : "Your access has no end date."}
            </span>
          )}
        </div>
        {kind !== "complimentary" && e.status !== "none" && (
          <button type="button" className="button" onClick={() => void manage()}>
            Manage billing
          </button>
        )}
        {!e.active && (
          <button type="button" className="button button--primary" onClick={onSubscribe}>
            Choose a plan
          </button>
        )}
      </section>
      {meters.length > 0 && (
        <section className="account__month" aria-label="This month">
          <p className="eyebrow account__eyebrow">
            This month{resets ? ` · resets ${dayMonth(resets)}` : ""}
          </p>
          <div className="account__meters">
            {meters.map((m) => (
              <MeterRow key={m.name} m={m} />
            ))}
          </div>
          <p className="account__small">
            Held allowance is reserved while flashcards are being made or a paper is being written.
            If the work fails, it comes back.
          </p>
        </section>
      )}
      <p className="account__lapse">
        <strong>If your plan ends,</strong> everything you made stays here to study: every
        flashcard, lesson and paper. You just can’t upload or write new papers.
      </p>
    </main>
  );
}
